import React, { useMemo, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import CollapsibleCard from '@core/components/CollapsibleCard';
import { useUserAccounts } from '@features/entity/hooks/useUserAccounts';
import { EntityScope, UserAccount } from '@features/entity/types/entity';
import { getApiScopeFromValue } from '@features/invitations/utils/getScopeFromParam';
import { entityNameCacheKey } from '@features/auth/constants/sessionStorageKeys';
import './MyPositionsSection.css';

const SCOPE_ORDER: EntityScope[] = ['NATION', 'DISTRICT', 'CHURCH'];

const SCOPE_LABELS: Record<EntityScope, { one: string; many: string }> = {
  NATION: { one: 'Nation', many: 'Nations' },
  DISTRICT: { one: 'District', many: 'Districts' },
  CHURCH: { one: 'Church', many: 'Churches' },
};

export function groupAccountsByScope(accounts: UserAccount[]) {
  return SCOPE_ORDER.map((scope) => {
    const items = accounts
      .filter((account) => account.scope === scope)
      .sort((a, b) => a.scope_name.localeCompare(b.scope_name));
    const labels = SCOPE_LABELS[scope];
    return { scope, label: items.length === 1 ? labels.one : labels.many, items };
  }).filter((group) => group.items.length > 0);
}

export function positionLabel(profile: UserAccount['profiles'][number]): string {
  return profile.department_name
    ? `${profile.profile_description} \u00B7 ${profile.department_name}`
    : profile.profile_description;
}

/** Read-only summary of where the user serves and the positions they hold. Hidden when there is nothing to show. */
const MyPositionsSection: React.FC = () => {
  const listRef = useRef<HTMLDivElement>(null);
  const { accounts, total, hasMore, canShowLess, loadMore, showLess, loading, loadingMore, error, loadMoreError } =
    useUserAccounts({ listRef });

  const groups = useMemo(() => groupAccountsByScope(accounts), [accounts]);

  if (loading || error || groups.length === 0) {
    return null;
  }

  return (
    <CollapsibleCard
      className="ntcog-card dashboard-section-card my-positions mb-4"
      title="My positions"
      subtitle="Where you serve and the positions you hold."
      bodyId="profile-my-positions-body"
    >
      <div ref={listRef} className="my-positions__list">
      {groups.map((group) => (
        <section key={group.scope} className="my-positions__group" data-testid={`my-positions-group-${group.scope}`}>
          <h3 className="my-positions__group-label">{group.label}</h3>
          <div className="my-positions__grid">
            {group.items.map((account) => (
              <div key={`${account.scope}-${account.scope_ref_id}`} className="my-positions__card">
                <Link
                  to={`/${getApiScopeFromValue(account.scope)}/${account.scope_ref_id}/dashboard`}
                  className="my-positions__name"
                  onClick={() =>
                    sessionStorage.setItem(entityNameCacheKey(account.scope, account.scope_ref_id), account.scope_name)
                  }
                >
                  {account.scope_name}
                </Link>
                <ul className="my-positions__roles">
                  {account.profiles.map((profile, index) => (
                    <li key={`${profile.profile_code}-${profile.department_id ?? index}`}>{positionLabel(profile)}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      ))}
      </div>

      {(hasMore || canShowLess) && (
        <div className="my-positions__more" aria-live="polite">
          <p className="my-positions__more-count">
            Showing {accounts.length} of {total} positions
          </p>
          <div className="my-positions__more-actions">
            {hasMore && (
              <Button variant="outline-primary" size="sm" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? 'Loading???' : 'Show more'}
              </Button>
            )}
            {canShowLess && (
              <Button variant="outline-secondary" size="sm" onClick={showLess} disabled={loadingMore}>
                Show less
              </Button>
            )}
          </div>
          {loadMoreError && <p className="my-positions__more-error">{loadMoreError}</p>}
        </div>
      )}
    </CollapsibleCard>
  );
};

export default MyPositionsSection;
